import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { alertDaysProblem, normalizeAlertDays } from "../lib/records";
import { LoadError, SaveBar, SectionHeader } from "./Settings";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";

// Paramètres › Documents — when the obligations' WhatsApp alerts go out (Ordre 18-3, D-41: 30 and 7 days before, then the due day),
// whether the people who approve documents get them too, and the company's identity the document templates use (18-4, D-62: empty
// until the direction fills it in). settings:view to read, settings:manage to change.
const COMPANY_FIELDS = [
  ["company.name", "Nom de la société", "Ex. Jamora SAS", 160],
  ["company.legal_form", "Forme juridique et capital", "Ex. SAS au capital de 1 000 000 FCFA", 160],
  ["company.address", "Adresse du siège", "", 300],
  ["company.identifiers", "Immatriculations", "Ex. RCCM SN-DKR-…, NINEA …", 300],
  ["company.representative", "Représentant légal", "Nom et qualité", 160],
];
export default function SettingsRecords() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [days, setDays] = useState("");
  const [approvers, setApprovers] = useState(true);
  const [company, setCompany] = useState({});
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const apply = (settings) => {
    setSaved(settings);
    setDays(settings.alert_days);
    setApprovers(!!settings.alert_approvers);
    setCompany(Object.fromEntries(COMPANY_FIELDS.map(([key]) => [key, settings[key] || ""])));
  };
  const companyChanges = saved == null ? {} : Object.fromEntries(COMPANY_FIELDS.map(([key]) => [key, (company[key] || "").trim()])
    .filter(([key, value]) => value !== (saved[key] || "")));
  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/records");
      apply(data.settings);
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const problem = alertDaysProblem(days);
  const dirty = saved != null && (normalizeAlertDays(days) !== saved.alert_days || approvers !== !!saved.alert_approvers
    || Object.keys(companyChanges).length > 0);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/records", { alert_days: normalizeAlertDays(days), alert_approvers: approvers, ...companyChanges });
      apply(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-records">
      <SectionHeader title="Documents" description="Les alertes des obligations à échéance et l'identité de la société utilisée par les modèles de documents." />
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="records-alert-days" className="text-sm font-medium text-slate-900">Prévenir avant la date limite</label>
          <div className="flex flex-wrap items-center gap-2">
            <Input id="records-alert-days" value={days} disabled={!canManage} className="w-40" onChange={(e) => setDays(e.target.value)}
              aria-invalid={!!problem} data-testid="records-alert-days" />
            <span className="text-sm text-slate-500">jours avant, séparés par des virgules (le jour même s'y ajoute toujours)</span>
          </div>
          {problem && <p className="text-xs text-red-600" role="alert">{problem}</p>}
        </div>
        <label className="flex items-start gap-3 border-t border-slate-100 pt-4">
          <Switch checked={approvers} onCheckedChange={setApprovers} disabled={!canManage} data-testid="records-alert-approvers" />
          <span className="text-sm">
            <span className="font-medium text-slate-900">Prévenir aussi les personnes qui approuvent les documents</span>
            <span className="block text-xs text-slate-500">Le responsable de l'obligation est toujours prévenu.</span>
          </span>
        </label>
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4 border-t border-slate-100 pt-3">
          <li>Le message donne le titre de l'obligation, sa date et un lien, jamais le contenu d'un document ; pour un document restreint, seulement la date.</li>
          <li>Une personne qui ne peut pas voir le document lié n'est pas prévenue. Chaque alerte ne part qu'une fois ; une nouvelle date en déclenche de nouvelles.</li>
          <li>Le canal WhatsApp et l'événement « Obligation qui arrive à échéance » se règlent dans Notifications.</li>
        </ul>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4" data-testid="records-company">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Identité de la société</h3>
          <p className="text-xs text-slate-500">Utilisée par les modèles de documents ({"{societe.nom}"}, {"{societe.adresse}"}…). Tant qu'un champ utilisé est vide, le modèle ne génère pas de document.</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {COMPANY_FIELDS.map(([key, label, placeholder, max]) => (
            <div key={key} className={`space-y-1.5 ${key === "company.address" || key === "company.identifiers" ? "md:col-span-2" : ""}`}>
              <label htmlFor={`records-${key}`} className="text-sm font-medium text-slate-900">{label}</label>
              <Input id={`records-${key}`} value={company[key] || ""} disabled={!canManage} maxLength={max} placeholder={placeholder}
                onChange={(e) => setCompany((c) => ({ ...c, [key]: e.target.value }))} data-testid={`records-${key.replace(".", "-")}`} />
            </div>
          ))}
        </div>
      </div>
      <SaveBar dirty={dirty} saveDisabled={!!problem} saving={saving} canManage={canManage} onSave={save} onRevert={() => apply(saved)} />
    </div>
  );
}
